import type { ProfilePhoto } from '@/types/models';

export function createPhotoId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(10));
    return `${Date.now().toString(36)}${Array.from(bytes, byte => byte.toString(36)).join('')}`;
  }
  return Date.now().toString(36);
}

export function storagePathFromUrl(url: string): string | undefined {
  if (url.startsWith('gs://')) {
    const path = url.slice('gs://'.length).split('/').slice(1).join('/');
    return path || undefined;
  }
  if (!url.includes('firebasestorage.googleapis.com')) return undefined;
  const match = url.match(/\/o\/([^?]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Coerce profile photo fields into photo objects. Tolerates legacy shapes
 * (bare URL strings, `{ url }` objects) during rollout; entries without a
 * stored `id` fall back to the URL itself so identity stays stable across
 * reads until the migration backfills real IDs. Entries without a stored
 * `position` get their index in the normalized array.
 */
export function normalizePhotos(photos: unknown): ProfilePhoto[] {
  return normalizePhotoEntries(photos).map((photo, index) =>
    photo.position == null ? { ...photo, position: index } : photo
  );
}

function photoFromUrl(value: string): ProfilePhoto[] {
  const url = value.trim();
  if (!url) return [];
  const path = storagePathFromUrl(url);
  return [{ id: url, url, ...(path ? { path } : {}) }];
}

function photoFromRecord(record: Record<string, unknown>): ProfilePhoto[] {
  if (typeof record.url !== 'string' || !record.url.trim()) {
    return Object.values(record).flatMap(value => normalizePhotoEntries(value));
  }

  const url = record.url.trim();
  const path =
    typeof record.path === 'string' && record.path ? record.path : storagePathFromUrl(url);
  return [
    {
      id: typeof record.id === 'string' && record.id ? record.id : url,
      url,
      ...(path ? { path } : {}),
      ...(typeof record.position === 'number' && Number.isFinite(record.position)
        ? { position: record.position }
        : {}),
      ...(typeof record.createdAt === 'string' && record.createdAt
        ? { createdAt: record.createdAt }
        : {}),
    },
  ];
}

function normalizePhotoEntries(photos: unknown): ProfilePhoto[] {
  if (!photos) return [];
  if (typeof photos === 'string') return photoFromUrl(photos);
  if (Array.isArray(photos)) return photos.flatMap(normalizePhotoEntries);
  if (typeof photos === 'object') {
    return photoFromRecord(photos as Record<string, unknown>);
  }
  return [];
}

export function firstPhotoUrl(photos: unknown): string | undefined {
  return normalizePhotos(photos)[0]?.url;
}

/**
 * Move the photo at `from` to index `to` and reindex `position`.
 * Out-of-range indices are clamped (drops on empty grid slots).
 */
export function reorderPhotos(photos: unknown, from: number, to: number): ProfilePhoto[] {
  const list = normalizePhotos(photos);
  if (list.length < 2 || from < 0 || from >= list.length) return list;
  const target = Math.max(0, Math.min(to, list.length - 1));
  const next = list.slice();
  const [moved] = next.splice(from, 1);
  next.splice(target, 0, moved);
  return next.map((photo, position) => ({ ...photo, position }));
}

export function removedPhotos(before: unknown, after: unknown): ProfilePhoto[] {
  const next = normalizePhotos(after);
  return normalizePhotos(before).filter(
    photo => !next.some(kept => kept.id === photo.id || kept.url === photo.url)
  );
}

export function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}
